import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

import DuellyTokenUserWallet from '@/components/DuellyTokenUserWallet.vue';
import AdminService from '@/services/AdminService';
import { duellyEventEmitter } from '@/services/eventBus';
import WebSocketService from '@/services/WebSocketService';
import { useAuthStore } from '@/store/authStore';
import { useWebSocketStore } from '@/store/webSocketStore';

const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        redirect: '/post-feed-view', // <-- Redirect root to /feed
        meta: { requiresAuth: false },
    },
    {
        path: '/privacy-policy',
        name: 'PrivacyPolicy',
        component: () =>
            import(/* webpackChunkName: "PrivacyPolicyView" */ '../views/PrivacyPolicyView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/search',
        name: 'search',
        component: () => import(/* webpackChunkName: "SearchView" */ '../views/SearchView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/search/results',
        name: 'search-results',
        component: () =>
            import(/* webpackChunkName: "SearchResultsView" */ '../views/SearchResultsView.vue'),
        meta: { requiresAuth: false },
    },
    {
        path: '/post-feed-view',
        name: 'post-feed-view',
        component: () => import(/* webpackChunkName: "PostFeedView" */ '../views/PostFeedView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/feed',
        name: 'feed',
        component: () => import(/* webpackChunkName: "PostFeedView" */ '../views/PostFeedView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/search',
        name: 'search',
        component: () => import(/* webpackChunkName: "SearchView" */ '../views/SearchView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/wallet',
        name: 'wallet',
        component: DuellyTokenUserWallet,
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/discord/callback',
        name: 'discord-callback',
        component: () =>
            import(
                /* webpackChunkName: "DiscordLoginCallback" */ '../views/DiscordLoginCallback.vue'
            ),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/google/callback',
        name: 'google-callback',
        component: () =>
            import(
                /* webpackChunkName: "GoogleLoginCallBack" */ '../views/GoogleLoginCallBack.vue'
            ),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/confirm-login',
        name: 'confirm-login',
        component: () => import(/* webpackChunkName: "profile" */ '../views/ConfirmLoginView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/user-management',
        name: 'user-management',
        component: () =>
            import(/* webpackChunkName: "user-management" */ '../views/UserManagement.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/profile',
        name: 'profile',
        component: () => import(/* webpackChunkName: "profile" */ '../views/ProfileView.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/public-profile',
        name: 'public-profile',
        component: () =>
            import(/* webpackChunkName: "PublicProfileView" */ '../views/PublicProfileView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/private-messages',
        name: 'private-messages',
        component: () =>
            import(
                /* webpackChunkName: "PrivateMessagesView" */ '../views/PrivateMessagesView.vue'
            ),
        meta: { requiresAuth: true }, // Public route
    },
    {
        path: '/private-inbox',
        name: 'private-inbox',
        component: () =>
            import(
                /* webpackChunkName: "PrivateMessagesInbox" */ '../views/PrivateMessagesInbox.vue'
            ),
        meta: { requiresAuth: true }, // Public route
    },

    {
        path: '/user-challenges',
        name: 'user-challenges',
        component: () =>
            import(/* webpackChunkName: "UserChallenges" */ '../views/UserChallenges.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/user-judged-challenges',
        name: 'user-judged-challenges',
        component: () =>
            import(
                /* webpackChunkName: "UserJudgedChallenges" */ '../views/UserJudgedChallenges.vue'
            ),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/user-responses',
        name: 'user-responses',
        component: () =>
            import(/* webpackChunkName: "UserResponses" */ '../views/UserResponses.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/user-posts',
        name: 'user-posts',
        component: () => import(/* webpackChunkName: "UserPosts" */ '../views/UserPosts.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/upload-flow',
        name: 'upload-flow',
        component: () => import(/* webpackChunkName: "UploaderFlow" */ '../views/UploaderFlow.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/user-post',
        name: 'user-post',
        component: () => import(/* webpackChunkName: "UserPost" */ '../views/UserPost.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/challenge-leaderboard',
        name: 'challenge-leaderboard',
        component: () =>
            import(
                /* webpackChunkName: "ChallengeLeaderBoard" */ '../views/ChallengeLeaderBoard.vue'
            ),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/challenge-final-board',
        name: 'challenge-final-board',
        component: () =>
            import(
                /* webpackChunkName: "ChallengeFinalBoard" */ '../views/ChallengeFinalBoard.vue'
            ),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/challenge-judge-final-board',
        name: 'challenge-judge-final-board',
        component: () =>
            import(
                /* webpackChunkName: "ChallengeJudgeFinalBoard" */ '../views/ChallengeJudgeFinalBoard.vue'
            ),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/user-challenge',
        name: 'user-challenge',
        component: () =>
            import(/* webpackChunkName: "UserChallenge" */ '../views/UserChallenge.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/user-response',
        name: 'user-response',
        component: () => import(/* webpackChunkName: "UserResponse" */ '../views/UserResponse.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/notifications',
        name: 'notifications',
        component: () =>
            import(/* webpackChunkName: "NotificationsView" */ '../views/NotificationsView.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/create-content',
        name: 'create-content',
        component: () =>
            import(/* webpackChunkName: "CreateContent" */ '../views/CreateContent.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/create-content-intro',
        name: 'create-content-intro',
        component: () =>
            import(/* webpackChunkName: "CreateContentIntro" */ '../views/CreateContentIntro.vue'),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/record-video',
        name: 'record-video',
        component: () =>
            import(
                /* webpackChunkName: "record-video" */ '../components/LiveRecordingComponent.vue'
            ),
        meta: { requiresAuth: true }, // Private route
    },
    {
        path: '/upload-video',
        name: 'upload-video',
        component: () =>
            import(/* webpackChunkName: "UploadComponent" */ '../components/UploadComponent.vue'),
        meta: { requiresAuth: true }, // Private route
    },

    {
        path: '/challenge-final-results',
        name: 'challenge-final-results',
        component: () =>
            import(/* webpackChunkName: "ChallengeResults" */ '../views/ChallengeResults.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/prize-details',
        name: 'prize-details',
        component: () =>
            import(/* webpackChunkName: "ChallengePrizeView" */ '../views/ChallengePrizeView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/qr-code',
        name: 'qr-code',
        component: () =>
            import(/* webpackChunkName: "InviteFriendsView" */ '../views/InviteFriendsView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/share-nostr-keys',
        name: 'share-nostr-keys',
        component: () =>
            import(/* webpackChunkName: "SharePrivateKeys" */ '../views/SharePrivateKeys.vue'),
        meta: { requiresAuth: true }, // Public route
    },
    {
        path: '/feedback',
        name: 'feedback',
        component: () =>
            import(/* webpackChunkName: "InviteFriendsView" */ '../views/FeedbackView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/send-user-push',
        name: 'send-user-push',
        component: () => import(/* webpackChunkName: "SendUserPush" */ '../views/SendUserPush.vue'),
        meta: { requiresAuth: false }, // Public route
    },

    // Admin realted routes
    {
        path: '/admin-dashboard',
        name: 'admin-dashboard',
        component: () =>
            import(
                /* webpackChunkName: "AdminDashbaordView" */ '../views/admin/AdminDashbaordView.vue'
            ),
        meta: { requiresAuth: true, requiresAdmin: true }, // Public route
    },
    {
        path: '/admin-user-dashboard',
        name: 'admin-user-dashboard',
        component: () =>
            import(
                /* webpackChunkName: "AdminDashbaordView" */ '../views/admin/AdminDashbaordView.vue'
            ),
        meta: { requiresAuth: true, requiresAdmin: true }, // Public route
    },
    // test for new gesture navigation
    {
        path: '/post-details',
        name: 'post-details',
        component: () =>
            import(/* webpackChunkName: "PostDetailsView" */ '../views/PostDetailsView.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    {
        path: '/public-profile2',
        name: 'ublic-profile2',
        component: () =>
            import(/* webpackChunkName: "PublicProfileView2" */ '../views/PublicProfileView2.vue'),
        meta: { requiresAuth: false }, // Public route
    },
    // {
    //     path: '/next-post',
    //     name: 'next-post',
    //     component: () => import(/* webpackChunkName: "NextPost" */ '../views/PublicProfileView2.vue'),
    //     meta: { requiresAuth: false }, // Public route
    // },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});

router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    const wsStore = useWebSocketStore();

    if (to.name === 'login') {
        return next({ name: 'post-feed-view', replace: true });
    }

    let accessToken = authStore.getAccessToken();
    if (authStore.authTokens.RefreshToken && authStore.isTokenExpiringSoon()) {
        console.log('Refreshing tokens from beforeEach');
        await authStore.refreshTokens();
        accessToken = authStore.getAccessToken();
    }

    // Allow public routes to be accessed without authentication
    if (to.meta.requiresAuth === false) {
        next(); // Proceed to the route as it's public
    }

    if (to.meta.requiresAdmin) {
        const isAdminUser = await AdminService.isAdminUser();

        if (!isAdminUser) {
            return next('/');
        }
        next(); // Proceed to the route as it's public
    }

    // For protected routes, ensure tokens are valid
    else if (to.meta.requiresAuth) {
        if (accessToken) {
            if (!WebSocketService.isActive()) {
                console.log('❌ Websocket not active - initializing');
                wsStore.close();
                wsStore.initialize(accessToken);
            } else {
                console.log('✅ Websocket already active');
            }
            next(); // Continue to route if tokens are valid
        } else {
            console.error('No valid access token found. Clearing Auth');
            // If no valid tokens, clear auth and show login overlay.
            authStore.clearAuth();
            wsStore.close();
            duellyEventEmitter.emit('show-login-overlay');

            // return an empty promise to avoid navigation and set redirect path in localStorage to use in loginOverlay or call backs
            return new Promise(() => {
                localStorage.setItem('redirectAfterLogin', to.fullPath);
            });
        }
    }
});

export default router;
